// MODE MOBILE — les fonctions qui n'ont de sens que sur un téléphone ou une
// tablette (capturer une photo, enregistrer un entretien sur place) ne
// s'affichent qu'en mode mobile.
//
// La MISE EN PAGE, elle, suit la largeur de l'écran (media queries) : un
// ordinateur en fenêtre étroite a la barre du bas, mais pas « Capturer ».
//
// Le mode se détecte tout seul. La détection peut se tromper (tablette avec
// clavier, navigateur qui se fait passer pour un ordinateur) : on peut alors le
// forcer depuis le menu du compte. Le choix vaut pour CE navigateur.
import { useSyncExternalStore } from 'react';
import { savePreference } from '../legal/cookieConsent';

export type DeviceSetting = 'auto' | 'mobile' | 'desktop';
const KEY = 'ro.device';

/** Ce que dit l'appareil lui-même, sans réglage. */
export function detectMobile(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile|Silk|Kindle/i.test(ua)) return true;
  // iPadOS se présente comme un Mac : seul l'écran tactile le trahit.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true;
  // Écran tactile sans souris : téléphone ou tablette, quel que soit le nom.
  return window.matchMedia?.('(pointer: coarse) and (hover: none)').matches ?? false;
}

// Le réglage tient en mémoire quand le stockage est refusé (bandeau cookies) :
// il vaut alors pour la page en cours.
let memory: DeviceSetting | null = null;
const listeners = new Set<() => void>();

export function deviceSetting(): DeviceSetting {
  if (memory) return memory;
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'mobile' || v === 'desktop') return v;
  } catch {
    /* stockage indisponible : détection automatique */
  }
  return 'auto';
}

export function setDeviceSetting(value: DeviceSetting) {
  memory = value;
  if (value === 'auto') {
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* rien à effacer */
    }
  } else {
    savePreference(KEY, value);
  }
  listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void) {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

/** Le réglage choisi (automatique, forcé mobile, forcé ordinateur). */
export function useDeviceSetting(): DeviceSetting {
  return useSyncExternalStore(subscribe, deviceSetting, () => 'auto' as const);
}

/** Est-on en mode mobile ? (réglage forcé, sinon détection) */
export function useIsMobile(): boolean {
  const setting = useDeviceSetting();
  return setting === 'auto' ? detectMobile() : setting === 'mobile';
}

/** La largeur sous laquelle la mise en page passe en « téléphone ». Même
 *  valeur que les media queries de `project.css`. */
export const NARROW_QUERY = '(max-width: 940px)';

function subscribeNarrow(notify: () => void) {
  const mq = window.matchMedia(NARROW_QUERY);
  mq.addEventListener('change', notify);
  return () => mq.removeEventListener('change', notify);
}

/** L'écran est-il étroit (mise en page téléphone) ? Suit le redimensionnement. */
export function useNarrowScreen(): boolean {
  return useSyncExternalStore(subscribeNarrow, () => window.matchMedia(NARROW_QUERY).matches, () => false);
}
